import {
	createRender,
	type HydrationResult,
	type BannerMessage,
	type MessageFrontendProps,
	type SuggestedFeature,
} from './types';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import CrossIcon from '@atlaskit/icon/core/cross';
import RocketIcon from './assets/rocket.svg';
import Image from '@atlaskit/image';
import LockIcon from './assets/lock-light.svg';
import LockIconDark from './assets/lock-dark.svg';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { StorageClient } from '@atlaskit/frontend-utilities';
import { BANNER_MESSAGES } from './constants';
import { type JSX } from 'react/jsx-runtime';

const LazyRecommendRetrialModal = lazy(() =>
	import(
		/* webpackChunkName: "async-recommend-retrial-modal" */
		'./recommend-retrial-modal'
	).then((module) => ({ default: module.RecommendRetrialModal })),
);

const recommendRetrialStorageClient = new StorageClient('jira-recommend-retrial-modal', {
	storageEngine: 'localStorage',
});
const SEEN_KEY = 'seen';

export const getLocalStorageSeenValue = (): boolean => {
	return recommendRetrialStorageClient.getItem(SEEN_KEY) === true;
};

export const setLocalStorageSeenValue = (): void => {
	recommendRetrialStorageClient.setItemWithExpiry(SEEN_KEY, true);
};

const bannerStyles = cssMap({
	banner: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.100'),
		transitionProperty: 'color, background-color',
		transitionDuration: '200ms',
	},
	bannerHovered: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	button: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.025'),
		textAlign: 'left',
		backgroundColor: 'transparent',
		width: '100%',
	},
});

const getBannerFormattedMessage = (
	bannerMessage: BannerMessage,
	suggestedFeature: SuggestedFeature,
) => BANNER_MESSAGES[suggestedFeature][bannerMessage];

const BannerIcon = ({
	suggestedFeature,
	isDarkMode,
}: {
	suggestedFeature: SuggestedFeature;
	isDarkMode: boolean;
}) => {
	if (suggestedFeature === 'Plans') {
		return <Image src={RocketIcon} alt="" height={32} width={32} />;
	}
	return <Image src={isDarkMode ? LockIconDark : LockIcon} alt="" height={32} width={32} />;
};

interface RecommendRetrialBannerProps {
	onCtaClick: () => void;
	onDismiss: () => void;
	suggestedFeature: SuggestedFeature;
	isDarkMode: boolean;
}

const RecommendRetrialBanner = ({
	onCtaClick,
	onDismiss,
	suggestedFeature,
	isDarkMode,
}: RecommendRetrialBannerProps) => {
	const [isHovered, setIsHovered] = useState(false);

	const onMouseEnter = useCallback(() => {
		setIsHovered(true);
	}, []);
	const onMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	return (
		<Flex
			xcss={cx(bannerStyles.banner, isHovered && bannerStyles.bannerHovered)}
			gap="space.200"
			justifyContent="space-between"
		>
			<Pressable
				xcss={bannerStyles.button}
				testId="jira-recommend-retrial-banner-cta"
				onClick={onCtaClick}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<BannerIcon suggestedFeature={suggestedFeature} isDarkMode={isDarkMode} />
				<Stack space="space.050">
					<Text size="medium" weight="medium">
						{getBannerFormattedMessage('heading', suggestedFeature)}
					</Text>
					<Text size="medium" weight="medium" color="color.link">
						{getBannerFormattedMessage('cta', suggestedFeature)}
					</Text>
				</Stack>
			</Pressable>
			<IconButton
				icon={CrossIcon}
				appearance="subtle"
				label="Close"
				isTooltipDisabled={false}
				onClick={onDismiss}
				spacing="compact"
			/>
		</Flex>
	);
};

const MessageFrontend = ({
	onDismissed,
	edition,
	placeOrderLiteHref,
	suggestedFeature,
	trialLength,
	hasPaymentDetails,
	changeOfferingUrl,
	permissionsUrl,
	shouldForceOpenModal,
	entitlementId,
}: MessageFrontendProps) => {
	// Lazy initializer: read storage only once at mount, not on every re-render.
	const [isOpen, setIsOpen] = useState(() => shouldForceOpenModal && !getLocalStorageSeenValue());
	const hasFiredBannerViewedRef = useRef(false);
	const { messageDismissed } = useMessageEvent();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	const sendModalViewedEvent = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'jiraRecommendRetrialModal',
			attributes: {
				edition,
				suggestedFeature,
				trialLength,
				hasPaymentDetails,
				entitlementId,
			},
		});
	}, [
		fireAnalyticsEvent,
		edition,
		suggestedFeature,
		trialLength,
		hasPaymentDetails,
		entitlementId,
	]);

	// Once the modal is open (auto or user-opened), mark as seen so it won't auto-open again after refresh.
	useEffect(() => {
		if (isOpen) {
			sendModalViewedEvent();
			!getLocalStorageSeenValue() && setLocalStorageSeenValue();
		}
	}, [isOpen, sendModalViewedEvent]);

	const handleOpen = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'bannerOpen',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: { actionType: 'open' },
			additionalMetadataFromFrontend: {},
		});
		setIsOpen(true);
	}, [messageInstanceClicked]);

	const handleClose = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'closeJiraRecommendRetrialModal',
			attributes: {
				edition,
				suggestedFeature,
				trialLength,
				hasPaymentDetails,
				entitlementId,
			},
		});
		setLocalStorageSeenValue();
		setIsOpen(false);
	}, [fireAnalyticsEvent, edition, suggestedFeature]);

	const handleOnComplete = useCallback(() => {
		messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'tryNow',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {
				completionStatus: 'success',
			},
		}).catch(() => {
			fireAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'messageInstanceClicked',
				actionSubjectId: 'jiraRecommendRetrialModal',
			});
		});

		switch (suggestedFeature) {
			case 'Permissions':
				if (!permissionsUrl) {
					// Worse case scenario is we don't know the permissions route, reloading them on the
					// new edition is better than not allowing them to proceed
					globalThis.window.location.reload();
					return;
				}
				globalThis.window.location.href = permissionsUrl;
				break;
			case 'Plans':
				globalThis.window.location.href = '/jira/plans?create-plan=true';
				break;
		}

		onDismissed();
	}, [messageInstanceClicked, fireAnalyticsEvent, onDismissed, suggestedFeature, permissionsUrl]);

	const handleDismissBanner = useCallback(() => {
		setLocalStorageSeenValue();
		void messageDismissed();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend: {},
		});
		onDismissed();
	}, [messageDismissed, messageInstanceClicked, onDismissed]);

	useEffect(() => {
		if (hasFiredBannerViewedRef.current) return;
		hasFiredBannerViewedRef.current = true;
		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'jiraRecommendRetrialBanner',
			attributes: {
				edition,
				suggestedFeature,
				trialLength,
				hasPaymentDetails,
				entitlementId,
			},
		});
	}, [
		messageInstanceViewed,
		fireAnalyticsEvent,
		edition,
		suggestedFeature,
		trialLength,
		hasPaymentDetails,
		entitlementId,
	]);

	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	return (
		<>
			<RecommendRetrialBanner
				onCtaClick={handleOpen}
				onDismiss={handleDismissBanner}
				suggestedFeature={suggestedFeature}
				isDarkMode={isDarkMode}
			/>
			{isOpen && (
				<Suspense fallback={null}>
					<LazyRecommendRetrialModal
						isOpen={isOpen}
						onClose={handleClose}
						onComplete={handleOnComplete}
						suggestedFeature={suggestedFeature}
						edition={edition}
						trialLength={trialLength}
						placeOrderLiteHref={placeOrderLiteHref}
						hasPaymentDetails={hasPaymentDetails}
						changeOfferingUrl={changeOfferingUrl}
					/>
				</Suspense>
			)}
		</>
	);
};

export const render: (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = createRender((props) => <MessageFrontend {...props} />);

export default render;
